/**
 * useSidebarPermissions Hook
 *
 * Maneja la lógica de permisos y filtrado de navegación
 *
 * User Types:
 * - platform: OWLY team members (internal platform administrators)
 * - tenant: Client company users (external CRM users)
 *
 * Scope Types:
 * - shared: Visible for both Platform and Tenant users
 * - platform: Only visible for Platform (OWLY) users
 * - tenant: Only visible for Tenant (client company) users
 */

import { useUser } from '@/shared/contexts/UserContext';
import { useAuth } from '@/shared/contexts/AuthContext';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { useTesting } from '@/shared/contexts/TestingContext';
import { NavigationSection, NavigationItem, Context, ModuleScope } from '../types/sidebar.types';
import { getVisibleSectionsForRole, isReadOnlyForRole } from '../utils/role-visibility.config';

export const useSidebarPermissions = (
  sections: NavigationSection[],
  currentContext: Context
) => {
  const { currentUser } = useUser();
  const { user: authUser } = useAuth();
  const { hasPermission, hasSubmodulePermission, isLoading: isLoadingPermissions } = usePermissions();
  const { config: testingConfig, owlyMode } = useTesting();

  // Determine if user is a PLATFORM user (OWLY team member)
  // Checks: backend user_type, testing owlyMode, or legacy sgiRole
  const isPlatformUserByType = authUser?.user_type === 'platform';
  const isPlatformUser = isPlatformUserByType || owlyMode || currentUser?.sgiRole?.startsWith('owly_') || false;

  const userJobTitle = currentUser?.jobTitle;

  // NOTE: is_superuser bypass has been REMOVED from business logic.
  // Superuser flag is now ONLY for Django Admin access.
  // All users (including admins) must have explicit role permissions.
  // const isSuperUser = authUser?.is_superuser === true; // REMOVED

  /**
   * Check if a scope is visible for the current user
   * @param scope - The scope to check ('shared' | 'platform' | 'tenant')
   * @param sectionScope - The section-level scope (for item inheritance)
   */
  const isScopeVisibleForUser = (scope?: ModuleScope | string, sectionScope?: ModuleScope | string): boolean => {
    // SECURITY: No more superuser bypass - all users follow permission rules

    // Determine effective scope (item scope overrides section scope)
    const effectiveScope = scope || sectionScope || 'shared';

    // Map legacy scopes to new system
    const normalizedScope = effectiveScope === 'platform-only' ? 'platform' :
                           effectiveScope === 'project-only' ? 'tenant' :
                           effectiveScope === 'context-dependent' ? 'shared' :
                           effectiveScope;

    switch (normalizedScope) {
      case 'shared':
        return true; // Everyone can see shared items
      case 'platform':
        return isPlatformUser; // Only platform users
      case 'tenant':
        return !isPlatformUser; // Only tenant users
      default:
        return true;
    }
  };

  /**
   * Filtrar secciones basado en scope, contexto y permisos
   */
  const filterSections = (): NavigationSection[] => {
    return sections.filter(section => {
      // Dev only sections
      if (section.devOnly && process.env.NODE_ENV !== 'development') {
        return false;
      }

      // OWLY only sections - only show when OWLY Mode is active or user is platform user
      // SECURITY: No superuser bypass - use isPlatformUser check instead
      if (section.owlyOnly && !owlyMode && !isPlatformUser) {
        return false;
      }

      // === UNIFIED SCOPE FILTERING ===
      // Check section scope visibility for current user
      // SECURITY: No superuser bypass - all users follow scope rules
      if (!isScopeVisibleForUser(section.scope as ModuleScope)) {
        return false;
      }

      // PLATFORM USERS (OWLY team): Show platform-only, shared, AND context-dependent sections
      // - platform-only: Platform administration sections
      // - shared: Sections available to all users (like Integrations)
      // - context-dependent: Sections that adapt based on global/project context
      if (isPlatformUser) {
        // Platform users see platform-only, shared, and context-dependent sections
        // Only 'project-only' sections are hidden (they require a project context)
        if (section.scope === 'project-only') {
          return false;
        }

        // Role-based visibility filtering for platform users (skip in testing OWLY Mode)
        if (!owlyMode) {
          const visibleSectionsForRole = getVisibleSectionsForRole(userJobTitle);
          if (visibleSectionsForRole.length > 0 && !visibleSectionsForRole.includes(section.id)) {
            return false;
          }
        }

        // Permission-based filtering for platform sections
        // ALL users (including OWLY_ADMIN and owlyMode) must validate permissions
        // NOTE: owlyMode NO bypasea permisos - solo indica si mostrar datos OWLY vs tenant
        if (section.requiredPermission && !hasPermission(section.requiredPermission)) {
          return false;
        }

        return true;
      }

      // TENANT USERS (client companies): Hide platform-only sections (Platform administration)
      if (section.scope === 'platform-only' || section.scope === 'platform') {
        return false;
      }

      // Scope filtering for tenant users
      if (section.scope === 'project-only' && currentContext.type !== 'project') {
        return false;
      }

      // Role-based filtering for tenant sections (check requiredRole at section level)
      if (section.requiredRole && authUser?.role !== section.requiredRole) {
        return false;
      }

      // Permission-based filtering for tenant sections
      if (section.requiredPermission) {
        if (!hasPermission(section.requiredPermission)) {
          return false;
        }
      }

      return true;
    });
  };

  const filteredSections = filterSections();

  /**
   * Filtrar items de una sección basado en contexto y permisos
   * @param items - Items de navegación a filtrar
   * @param sectionId - ID del módulo/sección padre (para verificar permisos de submódulo)
   * @param sectionScope - Scope de la sección padre ('platform-only', 'shared', etc.)
   */
  const filterItems = (items: NavigationItem[], sectionId?: string, sectionScope?: string): NavigationItem[] => {
    return items.filter(item => {
      // === UNIFIED SCOPE FILTERING ===
      // Check item scope visibility for current user
      // Item scope overrides section scope if specified
      // SECURITY: No superuser bypass - all users follow scope rules
      if (!isScopeVisibleForUser(item.scope as ModuleScope, sectionScope as ModuleScope)) {
        return false;
      }

      // PLATFORM USERS: Apply context filtering but ALSO validate permissions
      // ALL users (including platform) must have explicit permissions
      if (isPlatformUser) {
        // Platform-only sections: apply context filtering
        if (sectionScope === 'platform-only' || sectionScope === 'platform') {
          if (currentContext.type === 'global' && item.projectLabel && !item.globalLabel) {
            return false;
          }
          if (currentContext.type === 'project' && item.globalLabel && !item.projectLabel) {
            return false;
          }
          // NOW also validate permissions - owlyMode NO bypasea permisos
          if (item.requiredPermission && !hasPermission(item.requiredPermission)) {
            return false;
          }
          // Fall through to submodule permission check below
        }
        // For SHARED sections: apply permission checks normally (fall through)
      }

      // TENANT USERS & PLATFORM USERS (for non-platform sections):
      // Apply permission-based filtering

      // 1. Base permission check (e.g., 'leads.view')
      if (item.requiredPermission && !hasPermission(item.requiredPermission)) {
        return false;
      }

      // 2. Submodule visibility check - check module.{itemId} permission
      // The permissions page saves submodule permissions like 'leads.list', 'leads.funnel', etc.
      // SECURITY: hasSubmodulePermission uses DENY-BY-DEFAULT:
      // - Returns false if permissions haven't loaded
      // - Returns false if permission doesn't exist
      // - Returns true ONLY if permission is explicitly set to true
      // ALL users (including platform and owlyMode) must validate submodule permissions
      // NOTE: owlyMode ya NO bypasea permisos - los permisos siempre aplican
      //
      // EXCEPTION conocida: la seccion `integrations-module` agrupa items cuyo
      // requiredPermission pertenece a OTROS modulos backend (`integrations.view`,
      // `crm_migrations.view`). El submodule check fallaria con DENY-BY-DEFAULT
      // porque `backendPermissions['integrations-module']` no existe en backend.
      // En esa seccion, el `requiredPermission` ya gateado en lineas 193-195 es
      // el unico control. Cualquier OTRA seccion mantiene el submodule check
      // intacto (control granular del admin: ej. apagar `leads.list` lo oculta).
      const isKnownCrossModuleSection = sectionId === 'integrations-module';

      if (sectionId && item.id && !isKnownCrossModuleSection) {
        if (!hasSubmodulePermission(sectionId, item.id)) {
          return false;
        }
      }

      // Role-based filtering (Firebase auth role)
      if (item.requiredRole && authUser?.role !== item.requiredRole) {
        return false;
      }

      // If in global context
      if (currentContext.type === 'global') {
        // Hide project-only items
        if (item.projectLabel && !item.globalLabel) {
          return false;
        }
        return true;
      }

      // If in project context
      if (currentContext.type === 'project') {
        // Hide platform-only items
        if (item.globalLabel && !item.projectLabel) {
          return false;
        }
        return true;
      }

      return true;
    });
  };

  /**
   * Filtrar secciones que no tienen items visibles
   * Si todos los submódulos están apagados, el módulo tampoco debe aparecer
   */
  const sectionsWithVisibleItems = filteredSections.filter(section => {
    const visibleItems = filterItems(section.items, section.id, section.scope);
    return visibleItems.length > 0;
  });

  /**
   * Verificar si una sección es read-only para el rol actual
   */
  const checkIsReadOnly = (sectionId: string): boolean => {
    return isPlatformUser && isReadOnlyForRole(sectionId, userJobTitle);
  };

  return {
    visibleSections: sectionsWithVisibleItems,
    filterItems,
    checkIsReadOnly,
    isPlatformUser,
    testingEnabled: testingConfig.enabled,
    isLoadingPermissions,
  };
};
